import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { createPasswordsMatchValidator } from '../../../auth/services/helpers/passwordsMatchValidator';
import { Store } from '@ngrx/store';
import { filter, map, Observable, Subscription } from 'rxjs';
import { PasswordChangeStatus } from '../../store/user-password/user-password.state';
import { selectUserPasswordChangeError, selectUserPasswordChangeStatus } from '../../store/user-password/user-password.selectors';
import { userPasswordActions } from '../../store/user-password/user-password.actions';
import type { Breadcrumb } from '../../../core/models/breadcrumb.model';
import type { DeactivatableComponent } from '../../../core/guards/deactivatable.component.interface';
import { DeactivateDialogComponent } from '../../../shared/components/deactivate-dialog/deactivate-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ChangeUserPasswordResult } from '../../../core/services/user-settings.service';
import { createPasswordFormControl } from '../../../auth/services/helpers/createPasswordFormControl';

interface ChangePasswordFormData {
  oldPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
}

@Component({
  selector: 'app-change-password-view',
  templateUrl: './change-password-view.component.html',
  styleUrls: ['./change-password-view.component.scss'],
  standalone: false
})
export class ChangePasswordViewComponent implements OnInit, OnDestroy, DeactivatableComponent {
  protected status$: Observable<PasswordChangeStatus>;
  protected error$: Observable<string>;

  protected form: FormGroup<ChangePasswordFormData>;
  protected breadcrumbs: Breadcrumb[];

  private statusSubscription: Subscription;

  constructor(private store: Store, private dialog: MatDialog) {
    this.form = this.createForm();
    this.status$ = this.store.select(selectUserPasswordChangeStatus);
    this.error$ = this.store.select(selectUserPasswordChangeError).pipe(
      filter(v => !!v),
      map(status => {
        if (status === ChangeUserPasswordResult.IncorrectPassword) {
          return 'User.IncorrectOldPassword';
        }
        return 'User.PasswordChangeFailed';
      })
    );

    this.statusSubscription = this.status$.subscribe(status => {
      if (status === PasswordChangeStatus.Changed) {
        this.form.reset();
      }
    });

    this.breadcrumbs = [
      { url: '/projects', text: 'UI.Common.Breadcrumb.Projects', translatable: true, isCurrent: false },
      { url: '/projects/change-password', text: 'UI.Common.Breadcrumb.ChangePassword', translatable: true, isCurrent: true }
    ];
  }

  ngOnInit() {
    this.store.dispatch(userPasswordActions.password_change_page_open());
  }

  ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.form?.dirty) {
      return true;
    }

    return this.dialog
      .open(DeactivateDialogComponent, { disableClose: false, hasBackdrop: true })
      .afterClosed()
      .pipe(
        map(data => {
          if (data === undefined) {
            return false;
          }
          return data;
        })
      );
  }

  private createForm(): FormGroup<ChangePasswordFormData> {
    const oldPassword = new FormControl<string>('', { updateOn: 'change', validators: [Validators.required] });
    const newPassword = createPasswordFormControl('');
    const confirmPassword = new FormControl<string>('', { updateOn: 'change', validators: [Validators.required] });

    return new FormGroup(
      {
        oldPassword,
        newPassword,
        confirmPassword
      },
      { validators: [createPasswordsMatchValidator('newPassword', 'confirmPassword')] }
    );
  }

  protected save(): void {
    if (this.form.valid) {
      const oldPassword = this.form.controls.oldPassword.value;
      const newPassword = this.form.controls.newPassword.value;

      this.store.dispatch(userPasswordActions.password_change_start({ oldPassword, newPassword }));
    }
  }

  protected readonly PasswordChangeStatus = PasswordChangeStatus;
}
